import { useEffect } from 'react';
import { MessageType } from '../lib/senseiMessages';

interface MasterKaizenMessageProps {
  show: boolean;
  message: string;
  messageType?: MessageType;
  onClose: () => void;
  actionButton?: {
    label: string;
    onClick: () => void;
  };
  autoDismissMs?: number;
}

/**
 * Master Kaizen Sensei character modal component
 * Displays wisdom, encouragement, and guidance from the sensei
 */
export default function MasterKaizenMessage({
  show,
  message,
  messageType = 'guidance',
  onClose,
  actionButton,
  autoDismissMs = 5000,
}: MasterKaizenMessageProps) {
  useEffect(() => {
    if (show && autoDismissMs > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoDismissMs);
      return () => clearTimeout(timer);
    }
  }, [show, autoDismissMs, onClose]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [show, onClose]);

  if (!show) return null;

  const getGradientColors = () => {
    switch (messageType) {
      case 'celebration':
        return 'from-purple-50 to-yellow-50 dark:from-purple-900/30 dark:to-yellow-900/30';
      case 'encouragement':
        return 'from-blue-50 to-green-50 dark:from-blue-900/30 dark:to-green-900/30';
      case 'wisdom':
        return 'from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30';
      case 'guidance':
      default:
        return 'from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30';
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sensei-message-title"
    >
      <div
        className={`bg-gradient-to-r ${getGradientColors()} border-4 border-yellow-400 rounded-lg shadow-2xl p-8 max-w-md mx-4 animate-scale-in`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sensei Icon */}
        <div className="flex justify-center mb-6">
          <span className="text-8xl animate-bounce" aria-hidden="true">
            🧙
          </span>
        </div>

        {/* Title */}
        <h2
          id="sensei-message-title"
          className="text-2xl font-bold text-center mb-4 text-gray-800 dark:text-gray-100"
        >
          Master Kaizen
        </h2>

        {/* Message */}
        <p className="text-lg italic leading-relaxed text-center text-gray-700 dark:text-gray-200 mb-6">
          "{message}"
        </p>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4">
          {actionButton && (
            <button
              onClick={actionButton.onClick}
              className="px-6 py-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-colors"
            >
              {actionButton.label}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
          >
            Continue Training
          </button>
        </div>

        {/* Auto-dismiss indicator */}
        {autoDismissMs > 0 && (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
            Auto-closing in {autoDismissMs / 1000} seconds...
          </p>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
